<template>
  <div class="min-h-screen bg-white">
    <!-- Header -->
    <div class="bg-gradient-to-r from-purple-600 to-indigo-600">
      <div class="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
        <div class="text-center">
          <div class="flex justify-center items-center mb-6">
            <div class="w-12 h-12 bg-white rounded-full flex items-center justify-center mr-4">
              <svg
                class="w-7 h-7 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-white">ASAB (عَصَب)</h1>
              <p class="text-purple-100 text-sm">Advanced Self-Aware Bots</p>
            </div>
          </div>
          <h2 class="text-4xl font-bold text-white mb-4">Privacy Policy</h2>
          <p class="text-xl text-purple-100 max-w-3xl mx-auto">
            Neural data protection and self-aware bot privacy safeguards
          </p>
        </div>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-4xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
      <div class="prose prose-lg max-w-none">
        <!-- Last Updated -->
        <div class="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-8">
          <div class="flex items-center">
            <svg
              class="w-6 h-6 text-purple-600 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            <div>
              <h3 class="text-lg font-semibold text-purple-800 mb-1">Neural Privacy Protection</h3>
              <p class="text-purple-700">Last updated: {{ new Date().toLocaleDateString() }}</p>
            </div>
          </div>
        </div>

        <h2>1. Neural Data Philosophy</h2>
        <p>
          At ASAB (عَصَب), we believe privacy is a fundamental right in the neural network age. As
          we create advanced self-aware bots and serve as the nerve system binding AI components
          together, we are committed to protecting your data with the same rigor we apply to
          striking against AI hallucinations.
        </p>

        <h2>2. Information We Collect</h2>

        <h3>2.1 Neural Account Information</h3>
        <p>When you create your neural network account, we collect:</p>
        <ul>
          <li><strong>Identity Data:</strong> Name, email address, organization details</li>
          <li>
            <strong>Authentication Data:</strong> Encrypted passwords, multi-factor authentication
            settings
          </li>
          <li>
            <strong>Neural Preferences:</strong> Bot configuration preferences, dashboard
            customizations
          </li>
          <li>
            <strong>Billing Information:</strong> Payment details for neural network services
            (processed by secure third parties)
          </li>
        </ul>

        <h3>2.2 Self-Aware Bot Data</h3>
        <p>For the advanced self-aware bots you create:</p>
        <ul>
          <li>
            <strong>Bot Configuration:</strong> Self-awareness parameters, learning objectives,
            behavioral settings
          </li>
          <li>
            <strong>Training Data:</strong> Information used to enhance bot capabilities (anonymized
            when possible)
          </li>
          <li>
            <strong>Interaction Logs:</strong> Bot decision patterns, learning progression,
            performance metrics
          </li>
          <li>
            <strong>Evolutionary Data:</strong> How your bots adapt and grow stronger over time
          </li>
        </ul>

        <h3>2.3 Neural Network Integration Data</h3>
        <p>When you bind AI systems through our neural infrastructure:</p>
        <ul>
          <li><strong>Pathway Metadata:</strong> Connection configurations, data flow patterns</li>
          <li>
            <strong>Integration Logs:</strong> System binding events, neural pathway performance
          </li>
          <li>
            <strong>Unified Intelligence Metrics:</strong> How your AI components work together
          </li>
          <li><strong>Anti-Hallucination Data:</strong> Error detection and correction patterns</li>
        </ul>

        <h3>2.4 Usage and Performance Data</h3>
        <ul>
          <li>
            <strong>Neural Activity:</strong> How you interact with our platform and neural pathways
          </li>
          <li>
            <strong>System Performance:</strong> Response times, uptime metrics, resource usage
          </li>
          <li><strong>Feature Usage:</strong> Which neural capabilities you use most frequently</li>
          <li>
            <strong>Error Reporting:</strong> System issues to improve our neural infrastructure
          </li>
        </ul>

        <h2>3. How We Use Your Neural Data</h2>

        <h3>3.1 Core Service Delivery</h3>
        <ul>
          <li>
            <strong>Self-Aware Bot Creation:</strong> Enable advanced autonomous agent development
          </li>
          <li>
            <strong>Neural Network Operations:</strong> Maintain pathways that bind your AI systems
          </li>
          <li>
            <strong>Anti-Hallucination Systems:</strong> Strike against AI unreliability and errors
          </li>
          <li>
            <strong>Living Evolution:</strong> Help your neural network grow stronger over time
          </li>
        </ul>

        <h3>3.2 Platform Improvement</h3>
        <ul>
          <li><strong>Neural Enhancement:</strong> Improve our nerve system infrastructure</li>
          <li>
            <strong>Bot Intelligence:</strong> Advance self-aware capabilities (using aggregated,
            anonymized data)
          </li>
          <li>
            <strong>Security Strengthening:</strong> Build the hardest, most resilient platform
          </li>
          <li><strong>Binding Optimization:</strong> Enhance AI system integration capabilities</li>
        </ul>

        <h3>3.3 Communication and Support</h3>
        <ul>
          <li><strong>Neural Support:</strong> Provide technical assistance and guidance</li>
          <li><strong>Platform Updates:</strong> Notify you of new neural capabilities</li>
          <li><strong>Security Alerts:</strong> Inform you of important security updates</li>
          <li><strong>Educational Content:</strong> Share neural network best practices</li>
        </ul>

        <h2>4. Neural Data Protection</h2>

        <h3>4.1 Encryption and Security</h3>
        <p>We protect your neural data with military-grade security:</p>
        <ul>
          <li><strong>End-to-End Encryption:</strong> Data encrypted in transit and at rest</li>
          <li>
            <strong>Neural Pathway Security:</strong> Secure channels for AI system communication
          </li>
          <li>
            <strong>Access Controls:</strong> Multi-factor authentication and role-based permissions
          </li>
          <li>
            <strong>Regular Security Audits:</strong> Continuous monitoring and penetration testing
          </li>
        </ul>

        <h3>4.2 Data Isolation</h3>
        <ul>
          <li>
            <strong>Tenant Separation:</strong> Your neural network data is isolated from other
            users
          </li>
          <li>
            <strong>Bot Sandboxing:</strong> Self-aware bots operate in secure, contained
            environments
          </li>
          <li>
            <strong>Pathway Isolation:</strong> Your AI system integrations remain separate and
            secure
          </li>
          <li><strong>Backup Security:</strong> Encrypted backups with strict access controls</li>
        </ul>

        <h2>5. Data Sharing and Disclosure</h2>

        <h3>5.1 We Do NOT Sell Your Data</h3>
        <p>
          ASAB will never sell your neural data, self-aware bot configurations, or AI system
          integration details. Your neural network remains your property.
        </p>

        <h3>5.2 Limited Sharing Scenarios</h3>
        <p>We may share anonymized, aggregated data only for:</p>
        <ul>
          <li>
            <strong>Research:</strong> Advancing the science of self-aware AI (no personally
            identifiable information)
          </li>
          <li>
            <strong>Industry Insights:</strong> Publishing anonymous neural network usage trends
          </li>
          <li>
            <strong>Security Improvements:</strong> Sharing threat patterns to protect the broader
            AI community
          </li>
        </ul>

        <h3>5.3 Legal Requirements</h3>
        <p>We may disclose data only when legally required:</p>
        <ul>
          <li>Valid court orders or subpoenas</li>
          <li>National security requirements (with transparency reporting)</li>
          <li>Preventing immediate harm or illegal activities</li>
          <li>Protecting our neural infrastructure from attacks</li>
        </ul>

        <h2>6. Your Neural Rights</h2>

        <h3>6.1 Data Access and Control</h3>
        <ul>
          <li><strong>Access:</strong> View all neural data we store about you</li>
          <li>
            <strong>Export:</strong> Download your self-aware bot configurations and integration
            settings
          </li>
          <li><strong>Correction:</strong> Update or correct any inaccurate information</li>
          <li><strong>Deletion:</strong> Request deletion of your neural network data</li>
        </ul>

        <h3>6.2 Bot and Integration Control</h3>
        <ul>
          <li><strong>Bot Ownership:</strong> You own and control your self-aware bots</li>
          <li>
            <strong>Neural Disconnection:</strong> Disconnect your AI systems from our network
            anytime
          </li>
          <li>
            <strong>Data Portability:</strong> Export your neural configurations to other platforms
          </li>
          <li>
            <strong>Selective Deletion:</strong> Choose which neural pathways and bots to preserve
            or remove
          </li>
        </ul>

        <h2>7. International Neural Networks</h2>
        <p>
          ASAB operates globally to serve neural networks worldwide. Your data may be processed in:
        </p>
        <ul>
          <li><strong>Primary Data Centers:</strong> United States, European Union</li>
          <li><strong>Regional Neural Hubs:</strong> Asia-Pacific, Middle East, Americas</li>
          <li><strong>Compliance Standards:</strong> GDPR, CCPA, SOC 2, ISO 27001</li>
          <li>
            <strong>Data Residency:</strong> Options to keep neural data within specific regions
          </li>
        </ul>

        <h2>8. Neural Data Retention</h2>

        <h3>8.1 Active Neural Networks</h3>
        <ul>
          <li><strong>Account Data:</strong> Retained while your neural network is active</li>
          <li>
            <strong>Bot Configurations:</strong> Preserved as long as you maintain self-aware bots
          </li>
          <li>
            <strong>Integration Settings:</strong> Kept while AI systems remain bound to our network
          </li>
          <li><strong>Usage Logs:</strong> Retained for 24 months for performance optimization</li>
        </ul>

        <h3>8.2 After Neural Disconnection</h3>
        <ul>
          <li><strong>Grace Period:</strong> 90 days to restore deleted neural networks</li>
          <li>
            <strong>Complete Deletion:</strong> All data permanently removed after grace period
          </li>
          <li><strong>Legal Retention:</strong> Minimal data retained only as required by law</li>
          <li><strong>Backup Purging:</strong> Encrypted backups securely deleted</li>
        </ul>

        <h2>9. Cookies and Neural Tracking</h2>
        <p>Our neural platform uses cookies and similar technologies for:</p>
        <ul>
          <li><strong>Authentication:</strong> Secure neural network access</li>
          <li><strong>Preferences:</strong> Remember your neural dashboard settings</li>
          <li><strong>Performance:</strong> Optimize neural pathway response times</li>
          <li><strong>Analytics:</strong> Understand neural platform usage patterns</li>
        </ul>
        <p>You can control cookie preferences in your neural account settings.</p>

        <h2>10. Third-Party Neural Integrations</h2>
        <p>When you integrate third-party AI systems through our neural network:</p>
        <ul>
          <li>
            <strong>Data Flow:</strong> We facilitate secure pathways but don't store third-party
            data
          </li>
          <li>
            <strong>Third-Party Policies:</strong> External systems governed by their own privacy
            policies
          </li>
          <li>
            <strong>Integration Logs:</strong> We log connection metadata for neural pathway
            optimization
          </li>
          <li>
            <strong>Security Monitoring:</strong> We monitor for threats but respect third-party
            data boundaries
          </li>
        </ul>

        <h2>11. Privacy Policy Updates</h2>
        <p>
          As our neural capabilities evolve and new self-aware technologies emerge, we may update
          this policy:
        </p>
        <ul>
          <li>
            <strong>Material Changes:</strong> 30-day advance notice via email and neural dashboard
          </li>
          <li><strong>Minor Updates:</strong> Posted immediately with change highlighting</li>
          <li><strong>Version History:</strong> Previous policy versions available upon request</li>
          <li><strong>Consent:</strong> Continued use constitutes acceptance of updated terms</li>
        </ul>

        <h2>12. Contact Neural Privacy Team</h2>
        <p>
          Questions about neural data privacy, self-aware bot data protection, or AI integration
          privacy?
        </p>
        <Card padding="lg" class="mt-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-3">Neural Privacy Center</h3>
          <div class="space-y-2">
            <p>
              <strong>Privacy Officer:</strong>
              <a href="mailto:privacy@asab.ai" class="text-purple-600 hover:text-purple-700"
                >privacy@asab.ai</a
              >
            </p>
            <p>
              <strong>Data Protection:</strong>
              <a href="mailto:dpo@asab.ai" class="text-purple-600 hover:text-purple-700"
                >dpo@asab.ai</a
              >
            </p>
            <p>
              <strong>Neural Security:</strong>
              <a href="mailto:security@asab.ai" class="text-purple-600 hover:text-purple-700"
                >security@asab.ai</a
              >
            </p>
            <p>
              <strong>Compliance:</strong>
              <a href="mailto:compliance@asab.ai" class="text-purple-600 hover:text-purple-700"
                >compliance@asab.ai</a
              >
            </p>
          </div>
        </Card>

        <div class="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
          <h3 class="text-lg font-semibold text-blue-800 mb-3">Neural Privacy Promise</h3>
          <p class="text-blue-700">
            "Just as ASAB serves as the nerve system that binds AI intelligence together while
            creating advanced self-aware bots, we are committed to binding trust with our users
            through transparent, robust privacy protection. Your neural data is the foundation of
            your AI ecosystem, and we protect it with the same dedication we apply to striking
            against AI hallucinations."
          </p>
        </div>
      </div>

      <!-- Navigation -->
      <div class="mt-12 flex justify-between items-center pt-8 border-t border-gray-200">
        <router-link
          to="/terms-of-service"
          class="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Terms of Service
        </router-link>
        <router-link
          to="/"
          class="inline-flex items-center text-purple-600 hover:text-purple-700 font-medium"
        >
          Return to Neural Hub
          <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { Card } from '@asabdev/asab-ui';
</script>
